import { useState } from 'react'
import { signInWithGoogle, signInWithEmail, signUpWithEmail } from '../../services/auth'
import { CustomTextInput } from '../../components/common/CustomTextInput'
import { CustomButton } from '../../components/common/CustomButton'

type Mode = 'google' | 'email'
type EmailAction = 'signin' | 'signup'

export function Login() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [mode, setMode] = useState<Mode>('google')
  const [emailAction, setEmailAction] = useState<EmailAction>('signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [signupSuccess, setSignupSuccess] = useState(false)

  const handleGoogleSignIn = async () => {
    setLoading(true)
    setError(null)
    const { error } = await signInWithGoogle()
    if (error) {
      setError(error.message)
      setLoading(false)
    }
    // If successful, the page will redirect to Google
  }

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    setSignupSuccess(false)

    if (emailAction === 'signin') {
      const { error } = await signInWithEmail(email, password)
      if (error) {
        setError(error.message)
      }
      // On success, onAuthStateChange in App.tsx handles the rest
    } else {
      const { error } = await signUpWithEmail(email, password)
      if (error) {
        setError(error.message)
      } else {
        setSignupSuccess(true)
      }
    }
    setLoading(false)
  }

  const switchMode = (next: Mode) => {
    setMode(next)
    setError(null)
    setSignupSuccess(false)
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background-secondary">
      <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-card">
        <div className="mb-8 text-center">
          <div className="flex items-center justify-center gap-3">
            <img src="/appIcon.svg" alt="Barta" className="h-12 w-12 rounded-lg" />
            <h1 className="font-heading text-3xl font-bold text-primary">Admin</h1>
          </div>
          <p className="mt-2 text-gray-600">Sign in to access the admin panel</p>
        </div>

        {error && (
          <div className="mb-6 rounded-xl bg-red-50 p-4 text-sm text-red-700">
            {error}
          </div>
        )}

        {signupSuccess && (
          <div className="mb-6 rounded-xl bg-green-50 p-4 text-sm text-green-700">
            Account created. You can now sign in — but ask an admin to grant you super_admin via{' '}
            <code className="font-mono">make sb-superadmin EMAIL=&lt;your-email&gt;</code>.
          </div>
        )}

        {mode === 'google' ? (
          <>
            <button
              onClick={handleGoogleSignIn}
              disabled={loading}
              className="flex w-full items-center justify-center gap-3 rounded-xl border border-gray-300 bg-white px-4 py-3 text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              {loading ? 'Signing in...' : 'Sign in with Google'}
            </button>

            <div className="mt-4 text-center">
              <button
                onClick={() => switchMode('email')}
                className="text-sm text-[#7f4f9a] hover:underline"
              >
                Sign in with email instead
              </button>
            </div>
          </>
        ) : (
          <>
            <form onSubmit={handleEmailSubmit} className="flex flex-col gap-4">
              <CustomTextInput
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoComplete="email"
              />
              <CustomTextInput
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete={emailAction === 'signin' ? 'current-password' : 'new-password'}
              />
              <CustomButton type="submit" variant="primary" size="lg" loading={loading} className="w-full mt-1">
                {emailAction === 'signin' ? 'Sign in' : 'Sign up'}
              </CustomButton>
            </form>

            <div className="mt-4 flex flex-col items-center gap-2">
              <button
                onClick={() => {
                  setEmailAction(emailAction === 'signin' ? 'signup' : 'signin')
                  setError(null)
                  setSignupSuccess(false)
                }}
                className="text-sm text-gray-500 hover:underline"
              >
                {emailAction === 'signin'
                  ? "Don't have an account? Sign up"
                  : 'Already have an account? Sign in'}
              </button>
              <button
                onClick={() => switchMode('google')}
                className="text-sm text-[#7f4f9a] hover:underline"
              >
                Use Google instead
              </button>
            </div>
          </>
        )}

        <p className="mt-6 text-center text-xs text-gray-500">
          Only authorized administrators can access this panel.
        </p>
      </div>
    </div>
  )
}
